/**
 * Synapse Audio Upload Hook
 *
 * Integrates Synapse upload with the Voice Notes recording flow.
 * Uses atom-based state management via useSynapseUpload.
 *
 * This hook:
 * - Configures Synapse upload with auth and organization context
 * - Coordinates session lifecycle with recording start/stop
 * - Handles nucleus complete-upload call after preprocessing
 * - Manages UI state and analytics callbacks
 */
import { useCallback, useRef, useState } from 'react';

import { useOktaAuth } from '@okta/okta-react';
import {
  type AudioDeviceInfo,
  VideaNotesOPFS,
  liveTranscriptAtom,
  opfs,
  recordingSessionIdAtom,
  useSynapseUpload,
} from '@repo/videa-notes';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';

import configurations from '@/config';
import { endpoints } from '@/react-query/endpoints';
import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';
import { oktaAxiosInstance } from '@/utils/api';
import { formatPatientFirstNameAndLastName } from '@/utils/patient';
import { createDebugLogger } from '@videa/utils';

import { type NoteGenerationErrorProps, VideaNotesUIState } from '../types';

const debug = createDebugLogger('SynapseAudioUpload');

const { AMBIENT_API_URL } = configurations;

/** Retry configuration for nucleus API calls */
const NUCLEUS_RETRY_CONFIG = {
  maxRetries: 3,
  baseDelayMs: 1000,
  maxDelayMs: 10000,
};

/**
 * Retry a function with exponential backoff
 */
async function withRetry<T>(
  fn: () => Promise<T>,
  config: typeof NUCLEUS_RETRY_CONFIG,
  onRetry?: (attempt: number, error: Error) => void,
): Promise<T> {
  let lastError: Error | undefined;

  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      if (attempt < config.maxRetries) {
        const delay = Math.min(
          config.baseDelayMs * Math.pow(2, attempt),
          config.maxDelayMs,
        );
        debug.warn(
          `Retry attempt ${attempt + 1}/${config.maxRetries} after ${delay}ms:`,
          lastError.message,
        );
        onRetry?.(attempt + 1, lastError);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
}

interface UseSynapseAudioUploadProps {
  unifiedAppointmentId: string | undefined;
  standalonePatientVisitId: string | undefined;
  /** EstimatedVisit.id from the appointment data */
  estimatedVisitId: number | null | undefined;
  externalPatientId: string | undefined;
  pmsPatientId: string | undefined;
  imagingPatientId: string | undefined;
  /** BCP 47 language tag for the recording (e.g. 'en', 'es') */
  selectedLanguage: string;
  /** Patient name — sent as top-level field, encrypted server-side */
  patientName: string | undefined;
  currentOrganizationId: string;
  currentPracticeId: string;
  patientConsentRecordId: string | undefined;
  selectedPrompt: string | undefined;
  selectedTemplate: string | undefined;
  availableAudioDevices: AudioDeviceInfo[] | undefined;
  selectedAudioDeviceId: string | undefined;
  enableVoiceNotesConsent: boolean;
  enableVoiceNotesCustomTemplates: boolean;
  refetchClinicalNotes: () => Promise<{ data: ClinicalNote[] | undefined }>;
  refetchStandalonePatientVisitNotes: () => Promise<{
    data: ClinicalNote[] | undefined;
  }>;
  setUiStatus: (status: VideaNotesUIState) => void;
  setIsSheetOpen: (isOpen: boolean) => void;
  onSetSelectedNoteId: (noteId: string | undefined) => void;
  onGenerateSuccess: (generationTime: number) => void;
  onUploadError: () => void;
  onUploadSuccess: (s3UploadTime: number) => void;
}

interface StartAmbientSessionOptions {
  consentRecordId?: string;
  opfsSessionId?: string;
  recoveredAmbientSessionId?: string;
}

interface UseSynapseAudioUploadReturn {
  processingError: NoteGenerationErrorProps | undefined;
  startAmbientSession: (
    options?: StartAmbientSessionOptions,
  ) => Promise<string | null>;
  queueChunkForUpload: (
    chunkIndex: number,
    opfsSessionId: string,
  ) => Promise<void>;
  finishRecording: () => Promise<void>;
  ambientSessionId: string | null;
  isConfigured: boolean;
}

/**
 * Hook for uploading audio to Synapse Ambient service.
 *
 * This hook uses:
 * - Okta authentication (via oktaAuth.getOrRenewAccessToken)
 * - Chunk-based uploads to ambient.api.synapse.{env}.videahealth.io
 * - Background service worker for resilient uploads
 *
 * Flow:
 * 1. When recording starts, call startAmbientSession()
 * 2. As chunks are written to OPFS, call queueChunkForUpload(chunkIndex)
 * 3. When recording stops, call finishRecording()
 */
export const useSynapseAudioUpload = ({
  unifiedAppointmentId,
  standalonePatientVisitId,
  estimatedVisitId,
  externalPatientId,
  pmsPatientId,
  imagingPatientId,
  selectedLanguage,
  patientName,
  currentOrganizationId,
  currentPracticeId,
  patientConsentRecordId,
  selectedPrompt,
  selectedTemplate,
  availableAudioDevices,
  selectedAudioDeviceId,
  enableVoiceNotesConsent,
  enableVoiceNotesCustomTemplates,
  refetchClinicalNotes,
  refetchStandalonePatientVisitNotes,
  setUiStatus,
  setIsSheetOpen,
  onSetSelectedNoteId,
  onGenerateSuccess,
  onUploadError,
  onUploadSuccess,
}: UseSynapseAudioUploadProps): UseSynapseAudioUploadReturn => {
  const queryClient = useQueryClient();
  const { oktaAuth } = useOktaAuth();
  const [opfsSessionId, setOpfsSessionId] = useAtom(recordingSessionIdAtom);
  const liveTranscript = useAtomValue(liveTranscriptAtom);

  // Keep oktaAuth in a ref so the stable getToken callback always uses the
  // current instance without needing it in the dependency array.
  const oktaAuthRef = useRef(oktaAuth);
  oktaAuthRef.current = oktaAuth;

  // Token provider for Synapse API — calls getOrRenewAccessToken() on every
  // invocation so expired tokens are actively refreshed via the refresh token.
  // This mirrors the Axios interceptor approach (WithAxios.tsx) and works
  // correctly when the tab has been backgrounded and the cached token expired.
  const getToken = useCallback(async (): Promise<string> => {
    const token = await oktaAuthRef.current.getOrRenewAccessToken();
    if (!token) {
      throw new Error(
        'No Okta token available. Please ensure you are logged in.',
      );
    }
    return token;
  }, []);

  // Use the new atom-based Synapse upload hook
  const {
    isConfigured,
    ambientSessionId,
    error: synapseError,
    startSession,
    queueChunk,
    completeSession,
    clearSession,
  } = useSynapseUpload({
    synapseApiUrl: AMBIENT_API_URL,
    organizationId: currentOrganizationId,
    practiceId: currentPracticeId,
    getToken,
    enabled: Boolean(AMBIENT_API_URL),
  });

  // Track nucleus API errors separately from Synapse errors
  const [nucleusError, setNucleusError] =
    useState<NoteGenerationErrorProps | null>(null);

  // Cache OPFS recovery metadata from startAmbientSession so finishRecording
  // can use it even if the OPFS session is deleted by AudioRecorderProvider's
  // handleRecordingComplete before finishRecording reads it.
  // Only caches fields needed by finishRecording (nucleus complete-upload).
  // patientName is intentionally excluded — it's only needed at session start (Synapse metadata).
  const cachedRecoveryMetadataRef = useRef<{
    unifiedAppointmentId?: string;
    standalonePatientVisitId?: string;
    templateId?: string;
  } | null>(null);

  // Convert Synapse error to UI error format, or use nucleus error
  const processingError: NoteGenerationErrorProps | undefined = nucleusError
    ? nucleusError
    : synapseError
      ? { title: synapseError.title, description: synapseError.description }
      : undefined;

  /**
   * Start an ambient session when recording begins.
   */
  const startAmbientSession = useCallback(
    async (options?: StartAmbientSessionOptions): Promise<string | null> => {
      const effectiveConsentId =
        options?.consentRecordId ?? patientConsentRecordId;
      const effectiveOpfsSessionId = options?.opfsSessionId ?? opfsSessionId;
      const recoveredAmbientSessionId = options?.recoveredAmbientSessionId;

      if (!isConfigured) {
        debug.warn('Synapse not configured');
        return null;
      }

      // Check consent (skip for recovery)
      if (!recoveredAmbientSessionId) {
        const hasRequiredConsent =
          !enableVoiceNotesConsent || effectiveConsentId;
        if (!hasRequiredConsent) {
          debug.warn('Missing required patient consent');
          return null;
        }
      }

      // Get appointment IDs from props, or fall back to OPFS session metadata (for recovery)
      let effectiveUnifiedAppointmentId = unifiedAppointmentId;
      let effectiveStandalonePatientVisitId = standalonePatientVisitId;
      let effectiveExternalPatientId = externalPatientId;
      let effectivePmsPatientId = pmsPatientId;
      let effectiveImagingPatientId = imagingPatientId;
      let effectivePatientName = patientName;
      let effectiveEstimatedVisitId = estimatedVisitId;
      let effectiveTemplateId = enableVoiceNotesCustomTemplates
        ? selectedTemplate
        : undefined;
      // In recovery mode, props may not have appointment context - read from OPFS
      if (
        !effectiveUnifiedAppointmentId &&
        !effectiveStandalonePatientVisitId &&
        effectiveOpfsSessionId &&
        VideaNotesOPFS.isAvailable()
      ) {
        try {
          const session = await opfs.readSession(effectiveOpfsSessionId);
          if (session?.metadata) {
            effectiveUnifiedAppointmentId =
              session.metadata.unifiedAppointmentId ?? undefined;
            effectiveStandalonePatientVisitId =
              session.metadata.standalonePatientVisitId ?? undefined;
            effectiveExternalPatientId =
              session.metadata.externalPatientId ?? effectiveExternalPatientId;
            effectivePmsPatientId =
              session.metadata.pmsPatientId ?? effectivePmsPatientId;
            effectiveImagingPatientId =
              session.metadata.imagingPatientId ?? effectiveImagingPatientId;
            effectiveTemplateId =
              session.metadata.templateId ?? effectiveTemplateId;
            effectiveEstimatedVisitId =
              session.metadata.estimatedVisitId ?? effectiveEstimatedVisitId;
            // Recover patient name from OPFS (needed for /ambient standalone notes)
            if (!effectivePatientName) {
              const recoveredName =
                formatPatientFirstNameAndLastName(
                  session.metadata.patientFirstName,
                  session.metadata.patientLastName,
                ) ?? '';
              if (recoveredName) {
                effectivePatientName = recoveredName;
              }
            }
            // Cache recovery metadata so finishRecording can use it even if
            // the OPFS session is deleted before it reads (race with handleRecordingComplete)
            cachedRecoveryMetadataRef.current = {
              unifiedAppointmentId: effectiveUnifiedAppointmentId,
              standalonePatientVisitId: effectiveStandalonePatientVisitId,
              templateId: effectiveTemplateId,
            };
            debug.log(
              'Using metadata from OPFS session for startAmbientSession:',
              {
                unifiedAppointmentId: effectiveUnifiedAppointmentId,
                standalonePatientVisitId: effectiveStandalonePatientVisitId,
                patientName: effectivePatientName,
              },
            );
          }
        } catch (error) {
          debug.warn(
            'Failed to read OPFS session metadata for startAmbientSession:',
            error,
          );
        }
      }

      return startSession({
        sessionParams: {
          externalPatientId: effectiveExternalPatientId,
          pmsPatientId: effectivePmsPatientId,
          imagingPatientId: effectiveImagingPatientId,
          patientName: effectivePatientName,
          estimatedVisitId:
            effectiveEstimatedVisitId != null
              ? String(effectiveEstimatedVisitId)
              : undefined,
          unifiedAppointmentId: effectiveUnifiedAppointmentId,
          patientConsentRecordId: effectiveConsentId,
          mimeType: 'audio/webm',
          templateId: effectiveTemplateId,
          availableAudioDevices,
          selectedAudioDeviceId,
          ...(selectedLanguage &&
            selectedLanguage !== 'en' && {
              language: selectedLanguage,
            }),
        },
        opfsSessionId: effectiveOpfsSessionId ?? undefined,
        recoveredAmbientSessionId,
      });
    },
    [
      isConfigured,
      enableVoiceNotesConsent,
      patientConsentRecordId,
      opfsSessionId,
      startSession,
      externalPatientId,
      pmsPatientId,
      imagingPatientId,
      patientName,
      estimatedVisitId,
      unifiedAppointmentId,
      standalonePatientVisitId,
      enableVoiceNotesCustomTemplates,
      selectedTemplate,
      availableAudioDevices,
      selectedAudioDeviceId,
      selectedLanguage,
    ],
  );

  /**
   * Queue a chunk for background upload.
   */
  const queueChunkForUpload = useCallback(
    async (chunkIndex: number, sessionId: string): Promise<void> => {
      await queueChunk(chunkIndex, sessionId);
    },
    [queueChunk],
  );

  /**
   * Complete recording and trigger note generation.
   */
  const finishRecording = useCallback(async (): Promise<void> => {
    if (!ambientSessionId) {
      debug.warn('No ambient session to complete');
      return;
    }

    // Clear any previous nucleus error
    setNucleusError(null);

    try {
      // Step 1: Complete Synapse session (remux + upload to nucleus bucket)
      // Pass local STT transcript (Web Speech API) to weaver for storage
      const localTranscriptText =
        liveTranscript.length > 0 ? liveTranscript.join(' ') : undefined;
      const result = await completeSession(localTranscriptText);

      if (!result) {
        throw new Error('Failed to complete Synapse session');
      }

      onUploadSuccess(result.elapsedTime);

      // Step 2: Call nucleus complete-upload directly
      const nucleusStartTime = Date.now();

      // Get values from props, or fall back to OPFS session metadata (for recovery)
      let effectiveUnifiedAppointmentId = unifiedAppointmentId;
      let effectiveStandalonePatientVisitId = standalonePatientVisitId;
      let effectiveTemplateId = enableVoiceNotesCustomTemplates
        ? selectedTemplate
        : undefined;

      // In recovery mode, props may not have appointment context - read from OPFS
      if (
        !effectiveUnifiedAppointmentId &&
        !effectiveStandalonePatientVisitId &&
        opfsSessionId &&
        VideaNotesOPFS.isAvailable()
      ) {
        try {
          const session = await opfs.readSession(opfsSessionId);
          if (session?.metadata) {
            effectiveUnifiedAppointmentId =
              session.metadata.unifiedAppointmentId ?? undefined;
            effectiveStandalonePatientVisitId =
              session.metadata.standalonePatientVisitId ?? undefined;
            // Also recover template ID if not set from props
            if (!effectiveTemplateId && session.metadata.templateId) {
              effectiveTemplateId = session.metadata.templateId;
            }
            debug.log('Using metadata from OPFS session for complete-upload:', {
              unifiedAppointmentId: effectiveUnifiedAppointmentId,
              standalonePatientVisitId: effectiveStandalonePatientVisitId,
              templateId: effectiveTemplateId,
            });
          }
        } catch (error) {
          debug.warn(
            'Failed to read OPFS session metadata for complete-upload:',
            error,
          );
        }
      }

      // Fall back to cached recovery metadata if OPFS read failed or returned empty.
      // This handles the race where AudioRecorderProvider.handleRecordingComplete
      // deletes the OPFS session before finishRecording reads it.
      if (
        !effectiveUnifiedAppointmentId &&
        !effectiveStandalonePatientVisitId &&
        cachedRecoveryMetadataRef.current
      ) {
        effectiveUnifiedAppointmentId =
          cachedRecoveryMetadataRef.current.unifiedAppointmentId;
        effectiveStandalonePatientVisitId =
          cachedRecoveryMetadataRef.current.standalonePatientVisitId;
        if (!effectiveTemplateId) {
          effectiveTemplateId = cachedRecoveryMetadataRef.current.templateId;
        }
        debug.log('Using cached recovery metadata for complete-upload:', {
          unifiedAppointmentId: effectiveUnifiedAppointmentId,
          standalonePatientVisitId: effectiveStandalonePatientVisitId,
          templateId: effectiveTemplateId,
        });
      }

      const requestBody: Record<string, unknown> = {
        key: result.nucleusS3Key,
        uploadId: '',
        parts: [],
        skipMultipartCompletion: true,
        patientConsentRecordId,
        availableAudioDevices,
        selectedAudioDeviceId,
        language: selectedLanguage,
      };

      if (effectiveTemplateId) {
        requestBody.templateId = effectiveTemplateId;
      } else if (!enableVoiceNotesCustomTemplates && selectedPrompt) {
        requestBody.promptName = selectedPrompt;
      }

      if (effectiveUnifiedAppointmentId) {
        requestBody.unifiedAppointmentId = effectiveUnifiedAppointmentId;
      } else if (effectiveStandalonePatientVisitId) {
        requestBody.standalonePatientVisitId =
          effectiveStandalonePatientVisitId;
      }

      // Call nucleus complete-upload with retry logic
      const response = await withRetry(
        async () => {
          return oktaAxiosInstance.post<ClinicalNote>(
            `${configurations.API_URL}/videa-notes/complete-upload`,
            requestBody,
            {
              headers: {
                practice: currentPracticeId,
                org: currentOrganizationId,
              },
            },
          );
        },
        NUCLEUS_RETRY_CONFIG,
        (attempt, error) => {
          debug.warn(
            `Nucleus complete-upload retry ${attempt}:`,
            error.message,
          );
        },
      );

      const note = response.data;

      // Only attempt refetch if we have original props (not in recovery mode).
      // In recovery mode, the refetch hooks were created with undefined IDs
      // and will make requests to /appointment-notes/undefined which fails slowly.
      const isRecoveryMode =
        !unifiedAppointmentId && !standalonePatientVisitId && opfsSessionId;

      let updatedNotesData: ClinicalNote[] | undefined;
      if (!isRecoveryMode) {
        if (effectiveUnifiedAppointmentId) {
          const refetchResult = await refetchClinicalNotes();
          updatedNotesData = refetchResult.data;
        }
        if (effectiveStandalonePatientVisitId) {
          const refetchResult = await refetchStandalonePatientVisitNotes();
          updatedNotesData = refetchResult.data;
        }
      }

      // Calculate total generation time including nucleus API call
      const totalGenerationTime =
        result.elapsedTime + (Date.now() - nucleusStartTime);
      onGenerateSuccess(totalGenerationTime);

      // Invalidate queries
      queryClient.invalidateQueries({
        queryKey: [
          endpoints.ambient.voiceNotes.batchNotesByUnifiedAppointmentIds,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          endpoints.ambient.voiceNotes.batchNotesByStandalonePatientVisitIds,
        ],
      });

      // Set selected note - in recovery mode, use the note from nucleus directly
      if (updatedNotesData && updatedNotesData.length > 0) {
        const updatedNote =
          updatedNotesData.find((n) => n.id === note.id) || updatedNotesData[0];
        onSetSelectedNoteId(updatedNote.id);
      } else {
        onSetSelectedNoteId(note.id);
      }

      setUiStatus(VideaNotesUIState.SUMMARY_RESULT);

      // Clean up OPFS only after successful nucleus response
      if (opfsSessionId && VideaNotesOPFS.isAvailable()) {
        try {
          await opfs.deleteSession(opfsSessionId);
          setOpfsSessionId(null);
        } catch (cleanupError) {
          debug.error('Failed to clean up OPFS:', cleanupError);
        }
      }

      // Clear Synapse and recovery state
      clearSession();
      // eslint-disable-next-line require-atomic-updates -- ref is a stable React ref, not a shared variable across concurrent iterations
      cachedRecoveryMetadataRef.current = null;
    } catch (error) {
      debug.error('Failed to complete recording:', error);

      // Set appropriate error message
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      const isNucleusError =
        errorMessage !== 'Failed to complete Synapse session';
      if (isNucleusError) {
        setNucleusError({
          title: 'Note Generation Failed',
          description: `Failed to generate clinical note: ${errorMessage}`,
        });
      }

      setUiStatus(VideaNotesUIState.ERROR);
      onUploadError();
    } finally {
      setIsSheetOpen(true);
    }
  }, [
    ambientSessionId,
    completeSession,
    liveTranscript,
    onUploadSuccess,
    patientConsentRecordId,
    availableAudioDevices,
    selectedAudioDeviceId,
    enableVoiceNotesCustomTemplates,
    selectedTemplate,
    selectedLanguage,
    selectedPrompt,
    unifiedAppointmentId,
    standalonePatientVisitId,
    currentPracticeId,
    currentOrganizationId,
    refetchClinicalNotes,
    refetchStandalonePatientVisitNotes,
    onGenerateSuccess,
    queryClient,
    onSetSelectedNoteId,
    setUiStatus,
    opfsSessionId,
    setOpfsSessionId,
    clearSession,
    setIsSheetOpen,
    onUploadError,
  ]);

  return {
    processingError,
    startAmbientSession,
    queueChunkForUpload,
    finishRecording,
    ambientSessionId,
    isConfigured,
  };
};
